"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import clsx from "clsx";
import type { SyncResource, SyncResult } from "@/lib/sync";

// How often the "X นาทีที่แล้ว" label re-renders on its own.
const RELATIVE_TICK_MS = 30_000;
// Polling granularity for the optional interval mode — the actual sync
// cadence is gated by `intervalMs` measured from the last run.
const INTERVAL_CHECK_MS = 5_000;

function formatRelative(iso: string, now: number): string {
  const diffMs = now - new Date(iso).getTime();
  if (!Number.isFinite(diffMs)) return "—";
  const minutes = Math.floor(Math.max(0, diffMs) / 60_000);
  if (minutes < 1) return "เมื่อสักครู่";
  if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
  return `${Math.floor(hours / 24)} วันที่แล้ว`;
}

/**
 * Fires a background `POST /api/sync/<resource>` after mount (and optionally
 * on an interval while the tab is visible). The page itself always renders
 * from SQLite; when the sync reports `changed`, `router.refresh()` re-renders
 * the Server Components from the fresh DB rows without losing client state.
 * Renders a small inline status badge.
 */
export default function SyncOnMount({
  resource,
  initialLastSyncedAt,
  initialError = null,
  intervalMs,
}: {
  resource: SyncResource;
  initialLastSyncedAt: string | null;
  initialError?: string | null;
  intervalMs?: number;
}) {
  const router = useRouter();
  const [lastSyncedAt, setLastSyncedAt] = useState(initialLastSyncedAt);
  const [error, setError] = useState(initialError);
  // Starts true: a sync is fired right after mount, and it keeps the server
  // render and the first client render identical (no time-dependent text).
  const [syncing, setSyncing] = useState(true);
  // null until mounted — relative time is only computed client-side to avoid
  // a hydration mismatch.
  const [now, setNow] = useState<number | null>(null);

  const inFlight = useRef(false);
  const lastRunAt = useRef(0);
  const mounted = useRef(false);
  // Held in a ref so `runSync` (and the mount effect) don't depend on the
  // router object's identity — re-firing the initial sync after refresh().
  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  const runSync = useCallback(async () => {
    if (inFlight.current) return; // never overlap requests
    inFlight.current = true;
    lastRunAt.current = Date.now();
    // `syncing` is flipped on by the caller (initial state on mount, `check`
    // for repeats).
    try {
      const res = await fetch(`/api/sync/${resource}`, { method: "POST", cache: "no-store" });
      const body = (await res.json().catch(() => null)) as
        | (Partial<SyncResult> & { error?: string })
        | null;
      if (!mounted.current) return;
      if (!res.ok || !body) {
        setError(body?.error ?? `HTTP ${res.status}`);
        return;
      }
      if (body.last_synced_at !== undefined) setLastSyncedAt(body.last_synced_at);
      setError(
        body.reason === "error" || body.reason === "backoff"
          ? (body.error ?? "ซิงก์ไม่สำเร็จ")
          : null,
      );
      if (body.changed) routerRef.current.refresh();
    } catch {
      if (mounted.current) setError("เชื่อมต่อเซิร์ฟเวอร์ไม่ได้");
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setSyncing(false);
        setNow(Date.now());
      }
    }
  }, [resource]);

  // Initial sync, once per mount. Deferred to a macrotask so it runs as a
  // callback (not in the effect body), and so React Strict Mode's dev-only
  // mount/unmount/mount cycle cancels the first one instead of firing twice.
  useEffect(() => {
    mounted.current = true;
    const id = setTimeout(() => void runSync(), 0);
    return () => {
      mounted.current = false;
      clearTimeout(id);
    };
  }, [runSync]);

  // Keep the relative-time label ticking.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), RELATIVE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Optional repeat sync — only while the tab is visible, and immediately on
  // becoming visible again if the interval has already elapsed.
  useEffect(() => {
    if (!intervalMs || intervalMs <= 0) return;
    const check = () => {
      if (document.visibilityState !== "visible") return;
      if (inFlight.current || Date.now() - lastRunAt.current < intervalMs) return;
      setSyncing(true);
      void runSync();
    };
    const id = setInterval(check, Math.min(intervalMs, INTERVAL_CHECK_MS));
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", check);
    };
  }, [intervalMs, runSync]);

  const ago = lastSyncedAt && now != null ? formatRelative(lastSyncedAt, now) : null;
  let label: string;
  if (syncing) label = "กำลังซิงก์…";
  else if (error) label = ago ? `ซิงก์ไม่สำเร็จ · อัปเดต ${ago}` : "ซิงก์ไม่สำเร็จ";
  else if (ago) label = `อัปเดต ${ago}`;
  else label = "ยังไม่เคยซิงก์";

  return (
    <span
      role="status"
      aria-live="polite"
      title={error && !syncing ? error : undefined}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] tabular-nums",
        error && !syncing ? "text-down" : "text-muted",
      )}
    >
      <span
        aria-hidden
        className={clsx(
          "h-1.5 w-1.5 rounded-full",
          syncing ? "animate-pulse bg-muted" : error ? "bg-down" : lastSyncedAt ? "bg-up" : "bg-muted",
        )}
      />
      {label}
    </span>
  );
}
